import React, { Fragment } from 'react'

import cx from 'classnames'
import moment from 'moment'
import { changeFilterGroups, clearFilters, setActiveFilterId } from 'slices/filters'

import FilterTag from 'components/FilterTag'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { INACTIVE_FILTER } from 'hooks/useSavedFilters'
import {
  FilterableEntity,
  FilterConfigAndAvailableValues,
  FilterOperator,
  FilterPage,
  FilterPlurality,
  FilterValues,
  FilterValueType,
  getFilterConfig,
  operatorToString,
  removeFilter
} from 'utils/filters'
import { valuesToDateString, valueToDateString } from 'utils/formatDates'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

const ExtraActiveFiltersTag = ({ count }: { count: number }) => {
  return <span className={styles.extraFiltersTag}>{`+ ${count} more`}</span>
}

const buildFilterChipFromFilter = (
  filter: FilterValues,
  onClickRemove: () => void,
  filters: FilterValues[],
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  const config = getFilterConfig(filter, filterConfiguration)

  if (
    !config ||
    filter.values === undefined ||
    filter.values === null ||
    filter.values.length === 0
  ) {
    return
  }

  const totalExtraFilters = Math.max(filter.values.length - 2, 0)

  const renderFilterContents = () => {
    if (config.plurality === FilterPlurality.MULTIPLE) {
      return filter.values.slice(0, 2).map((item: any, i: number) => {
        const getLabel = () => {
          if (
            item.label === item.value &&
            (config.filterType === FilterableEntity.ACCOUNT_OWNERS ||
              config.attributeType === 'TEAMMEMBER')
          ) {
            return 'Deleted user'
          }
          return item.label
        }
        return (
          <Fragment key={item.label}>
            {/* If the user is deleted, show "Deleted user" instead */}
            <span key={i}>{getLabel()}</span>
            {i === 0 && filter.values.length > 1 && ` or `}
          </Fragment>
        )
      })
    } else if (config.valueType === FilterValueType.DATE) {
      if (
        filter.operator === FilterOperator.BETWEEN &&
        filter.values.startDate &&
        filter.values.endDate
      ) {
        return (
          <span className={utilityStyles.semiBoldWeight}>{valuesToDateString(filter.values)}</span>
        )
      } else {
        return (
          <span className={utilityStyles.semiBoldWeight}>
            {valueToDateString(moment(filter.values))}
          </span>
        )
      }
    } else if (config.valueType === FilterValueType.NUMBER) {
      return <span className={utilityStyles.semiBoldWeight}>{filter.values}</span>
    } else {
      return <span className={utilityStyles.semiBoldWeight}>{filter.values.label}</span>
    }
  }

  return (
    <FilterTag
      key={`${filter.filterType}-${filter.filterSubtype}`}
      className={cx(styles.alignmentMargin)}
      onClick={onClickRemove}>
      <div>
        <span className={utilityStyles.semiBoldWeight}>{`${config.filterDisplayName}`}</span>
        {` ${operatorToString(filter.operator)} `}
        {renderFilterContents()}
        {totalExtraFilters > 0 && config.valueType !== FilterValueType.DATE && (
          <>
            {' or '}
            <span
              className={
                utilityStyles.semiBoldWeight
              }>{`${totalExtraFilters} ${config.extraWord}`}</span>
          </>
        )}
      </div>
    </FilterTag>
  )
}

const ActiveFilters = ({
  filterConfiguration,
  filterPage,
  onFilterChange,
  onClear
}: {
  filterConfiguration: FilterConfigAndAvailableValues[]
  filterPage: FilterPage
  onFilterChange?: () => void
  onClear?: () => void
}) => {
  const { filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const filterGroups = filterGroupsByPage[filterPage]
  const dispatch = useAppDispatch()

  const onClearFilterGroups = () => {
    dispatch(clearFilters({}))
  }

  const isSomeFilterValid = filterGroups.some(filters =>
    filters.some(filter =>
      getFilterConfig(filter, filterConfiguration)?.getIsFilterValid(filter?.values)
    )
  )

  let totalChips = 0
  let chipsRendered = 0
  const renderAllVisibleChips = (chips: any[], i: number, arr: any[]) => {
    totalChips += chips.length

    const atLastItem = arr.length - 1 === i
    const leftoverChipsExist = totalChips - chipsRendered > 0
    const willOverflow = chipsRendered + chips.length > 3

    if (atLastItem && leftoverChipsExist && chipsRendered !== 0 && willOverflow) {
      return <ExtraActiveFiltersTag count={totalChips - chipsRendered} />
    } else if (chipsRendered >= 3) {
      return <></>
    } else if (chips.length > 3) {
      chipsRendered += 3
      return (
        <Fragment key={i}>
          {i > 0 && <span className={cx(styles.orStyle, utilityStyles.semiBoldWeight)}>or</span>}
          {chips.splice(0, 3)}
          {atLastItem && <ExtraActiveFiltersTag count={totalChips - chipsRendered} />}
        </Fragment>
      )
    } else {
      chipsRendered += chips.length
      return (
        <Fragment key={i}>
          {i > 0 && <span className={cx(styles.orStyle, utilityStyles.semiBoldWeight)}>or</span>}
          {chips}
        </Fragment>
      )
    }
  }

  return (
    <>
      {filterGroups
        .map((filters, groupIndex) =>
          filters.map((filter, filterIndex) => {
            const onClickRemove = () => {
              onFilterChange && onFilterChange()
              const nextGroups = removeFilter(filterGroups, groupIndex, filterIndex)
              dispatch(
                changeFilterGroups({
                  page: filterPage,
                  filterGroups: nextGroups
                })
              )

              dispatch(setActiveFilterId(INACTIVE_FILTER))
              onFilterChange && onFilterChange()
              // Clear last filter from store
              if (!filterGroupsByPage[filterPage][0]?.length) {
                onClearFilterGroups()
              }
            }

            return getFilterConfig(filter, filterConfiguration)?.getIsFilterValid(filter.values)
              ? buildFilterChipFromFilter(filter, onClickRemove, filters, filterConfiguration)
              : null
          })
        )
        .filter(chips => chips?.some(chip => chip !== null))
        .map(renderAllVisibleChips)}
      {isSomeFilterValid && (
        <div
          className={cx(styles.clearText, styles.alignmentMargin)}
          onClick={() => {
            onClear ? onClear() : onClearFilterGroups()
          }}>
          Clear all
        </div>
      )}
    </>
  )
}

export default ActiveFilters
